import { IBalusterGeometry, IRailingConfig, IRailingGeometry, IStairConfig, IStepGeometry, IVec3 } from '@stair-craft/common/util';

import { balusterCount, treadBalusterRuns } from '../shared-baluster-count/baluster-count';
import { railLineOffset } from '../shared-board-band/board-band';
import { IFlightRail, flightRail, flightRailY } from '../shared-flight-rail/flight-rail';
import { insideOutline, treadFootprint } from '../shared-tread-footprint/tread-footprint';

/** Step-derived anchor values, independent of railing side. */
interface ILShapeStepAnchors {
    hasFlight1: boolean;
    firstF1: IStepGeometry | null;
    entryZ: number;
    firstStepY: number;
}

/** Turn-level values (heights along the turn), independent of railing side. */
interface ILShapeTurnLevels {
    turnEntryY: number;
    landingY: number;
    riserH: number;
    isWinderTurn: boolean;
    turnPostY: number;
}

/** Side-independent inputs threaded through the per-side builder. */
interface ILShapeRailingBase extends ILShapeStepAnchors, ILShapeTurnLevels {
    rc: IRailingConfig;
    config: IStairConfig;
    sw: number;
    isRight: boolean;
    turnStartZ: number;
    flight1Steps: IStepGeometry[];
    turnSteps: IStepGeometry[];
    flight2Steps: IStepGeometry[];
}

/** Per-side working context shared by the L-shape railing block helpers. */
interface ILShapeRailingContext extends ILShapeRailingBase {
    side: 'left' | 'right';
    isOuter: boolean;
    halfPost: number;
    f1X: number;
    f2Z: number;
    f2StartX: number;
    f1hAz: number;
    f1hAy: number;
    f1hBz: number;
    f1hBy: number;
    /** +1 when flight 2 climbs toward +X, −1 toward −X. */
    xDir: number;
    /** The one handrail line over flight 2, along `xDir · x`. */
    f2Rail: IFlightRail;
    balusters: IBalusterGeometry[];
    posts: IBalusterGeometry[];
    handrailPoints: IVec3[];
}

/**
 * Resolves the step-derived anchor values (entry position, first-step level).
 * Extracted from `generateLShapeRailings` to keep its complexity low.
 */
function resolveStepAnchors(flight1Steps: IStepGeometry[], turnSteps: IStepGeometry[], flight2Steps: IStepGeometry[]): ILShapeStepAnchors {
    const hasFlight1: boolean = flight1Steps.length > 0;
    const firstF1: IStepGeometry | null = hasFlight1 ? (flight1Steps.at(0) ?? null) : null;
    const firstStep: IStepGeometry | null = firstF1 ?? turnSteps.at(0) ?? flight2Steps.at(0) ?? null;
    const entryZ: number = firstF1?.position.z ?? 0;
    const firstStepY: number = firstStep?.position.y ?? 0;
    return { hasFlight1, firstF1, entryZ, firstStepY };
}

/**
 * Resolves the turn heights (entry/landing Y, post Y, winder flag).
 * Extracted from `generateLShapeRailings` to keep its complexity low.
 */
function resolveTurnLevels(
    flight1Steps: IStepGeometry[],
    turnSteps: IStepGeometry[],
    firstStepY: number,
    config: IStairConfig
): ILShapeTurnLevels {
    const lastF1: IStepGeometry | null = flight1Steps.length > 0 ? flight1Steps[flight1Steps.length - 1] : null;
    // Y the user is at when entering the turn — top of flight 1 if it exists,
    // otherwise the first turn step's level (which the bottom post already anchors to).
    const turnEntryY: number = lastF1?.position.y ?? firstStepY;
    const entryRiser: number = lastF1?.riserHeight ?? turnSteps.at(0)?.riserHeight ?? 0;
    const landingY: number = turnEntryY + entryRiser;
    const isWinderTurn: boolean = config.turnType === 'winder' && turnSteps.length > 0;
    // At the turn edge the nosing line reaches the first turn tread's top — the landing or the
    // first winder — so flight 1's rail ends the rail height above it on either turn.
    const turnPostY: number = landingY;
    return { turnEntryY, landingY, isWinderTurn, turnPostY, riserH: entryRiser };
}

/** Center X of a step's tread. */
function centerX(step: IStepGeometry): number {
    return step.position.x + step.treadWidth / 2;
}

/** Flight 2's handrail height above a plan X. */
function flight2RailY(ctx: ILShapeRailingContext, x: number): number {
    return flightRailY(ctx.f2Rail, ctx.xDir * x);
}

/** A newel standing at `baseY` tall enough to carry the higher of its two rail ends. */
function newelHeight(ctx: ILShapeRailingContext, baseY: number, upperRailY: number): number {
    return Math.max(ctx.rc.height, upperRailY - baseY);
}

/**
 * Flight 2's rail line: from the rail height above the first tread's nose tip — a nose length
 * back from the turn edge — to the top of the top post.
 */
function lShapeFlight2Rail(base: ILShapeRailingBase, xDir: number, topPost: IBalusterGeometry): IFlightRail {
    const first: IStepGeometry = base.flight2Steps[0];
    const turnEdgeX: number = base.isRight ? base.sw : 0;
    return flightRail(first, xDir * turnEdgeX - first.noseLength, base.rc.height, {
        along: xDir * topPost.position.x,
        y: topPost.position.y + topPost.height,
    });
}

/**
 * Base Y of the winder step underneath a given path fraction. Extracted from `generateLShapeRailings` to keep its complexity low.
 */
function winderYAtFrac(turnSteps: IStepGeometry[], pathStartY: number, totalFrac: number): number {
    if (turnSteps.length === 0) {
        return pathStartY;
    }
    const idx: number = Math.min(turnSteps.length - 1, Math.max(0, Math.floor(totalFrac * turnSteps.length)));
    return turnSteps[idx].position.y;
}

/**
 * Flight-1 balusters, interpolated between the bottom post top and the turn entry.
 * Extracted from `generateLShapeRailings` to keep its complexity low.
 */
function pushFlight1Balusters(ctx: ILShapeRailingContext): void {
    if (!ctx.hasFlight1 || !ctx.firstF1) {
        return;
    }
    const rc: IRailingConfig = ctx.rc;
    const f1Td: number = ctx.firstF1.treadDepth;
    const f1SpanZ: number = ctx.f1hBz - ctx.f1hAz;
    const flight: { stringerType: IStairConfig['stringerType']; axis: 'z' } = { stringerType: ctx.config.stringerType, axis: 'z' };
    for (const bz of treadBalusterRuns(ctx.f1hAz + rc.postWidth / 2, ctx.f1hBz, ctx.flight1Steps, flight, rc)) {
        const si: number = Math.min(Math.floor((bz - ctx.firstF1.position.z) / f1Td), ctx.flight1Steps.length - 1);
        const baseY: number = ctx.flight1Steps[si].position.y;
        const frac: number = (bz - ctx.f1hAz) / f1SpanZ;
        const hY: number = ctx.f1hAy + frac * (ctx.f1hBy - ctx.f1hAy);
        ctx.balusters.push({ position: { x: ctx.f1X, y: baseY, z: bz }, height: Math.max(rc.height / 2, hY - baseY), rotation: 0 });
    }
}

/**
 * Outer winder turn: L-path handrail (Z wall → 90° corner → X wall) and its balusters.
 * Extracted from `generateLShapeRailings` to keep its complexity low.
 */
function pushWinderTurnRailing(ctx: ILShapeRailingContext): void {
    const rc: IRailingConfig = ctx.rc;
    const { f1X, f2Z, turnStartZ, turnPostY, isRight, sw, halfPost } = ctx;

    // Smooth transition: start where flight 1's rail ends (no jump)
    ctx.handrailPoints.push({ x: f1X, y: turnPostY + rc.height, z: turnStartZ });

    // L-path: along Z wall → 90° corner → along X wall
    const cornerX: number = f1X;
    const cornerZ: number = f2Z;
    const endX: number = isRight ? sw - halfPost : halfPost;

    // L-path railing following rectangular walls (90° corner); it ends on flight 2's rail line,
    // so flight 2 goes on at the same height as the inner rail.
    const pathStartY: number = turnPostY;
    const pathEndY: number = flight2RailY(ctx, endX) - rc.height;

    const seg1Len: number = cornerZ - turnStartZ;
    const seg2Len: number = Math.abs(endX - cornerX);
    const totalLen: number = seg1Len + seg2Len;
    const xDir: number = isRight ? 1 : -1;

    // Handrail points along L-path
    const pathPointCount: number = Math.max(6, Math.round(totalLen / 80));
    for (let i: number = 0; i <= pathPointCount; i++) {
        const frac: number = i / pathPointCount;
        const dist: number = frac * totalLen;
        const y: number = pathStartY + frac * (pathEndY - pathStartY) + rc.height;

        if (dist <= seg1Len) {
            ctx.handrailPoints.push({ x: cornerX, z: turnStartZ + dist, y });
        } else {
            ctx.handrailPoints.push({ x: cornerX + xDir * (dist - seg1Len), z: cornerZ, y });
        }
    }

    // Balusters along segment 1 (Z wall)
    const seg1BalCount: number = balusterCount(seg1Len, rc);
    for (let i: number = 0; i < seg1BalCount; i++) {
        const frac: number = (i + 0.5) / seg1BalCount;
        const totalFrac: number = (seg1Len * frac) / totalLen;
        const handrailY: number = pathStartY + totalFrac * (pathEndY - pathStartY) + rc.height;
        const baseY: number = winderYAtFrac(ctx.turnSteps, pathStartY, totalFrac);
        ctx.balusters.push({
            position: { x: cornerX, y: baseY, z: turnStartZ + seg1Len * frac },
            height: Math.max(rc.height / 2, handrailY - baseY),
            rotation: 0,
        });
    }

    // Balusters along segment 2 (X wall)
    const seg2BalCount: number = balusterCount(seg2Len, rc);
    for (let i: number = 0; i < seg2BalCount; i++) {
        const frac: number = (i + 0.5) / seg2BalCount;
        const totalFrac: number = (seg1Len + seg2Len * frac) / totalLen;
        const handrailY: number = pathStartY + totalFrac * (pathEndY - pathStartY) + rc.height;
        const baseY: number = winderYAtFrac(ctx.turnSteps, pathStartY, totalFrac);
        ctx.balusters.push({
            position: { x: cornerX + xDir * seg2Len * frac, y: baseY, z: cornerZ },
            height: Math.max(rc.height / 2, handrailY - baseY),
            rotation: 0,
        });
    }
}

/**
 * Outer landing turn: L-path along landing edges plus edge balusters.
 * Extracted from `generateLShapeRailings` to keep its complexity low.
 */
function pushLandingEdges(ctx: ILShapeRailingContext): void {
    const rc: IRailingConfig = ctx.rc;
    const { f1X, f2Z, f2StartX, landingY, turnStartZ } = ctx;

    // Landing: L-path along landing edges (inset by halfPost)
    ctx.handrailPoints.push({ x: f1X, y: landingY + rc.height, z: f2Z });
    ctx.posts.push({ position: { x: f1X, y: landingY, z: f2Z }, height: rc.height, rotation: 0 });

    // The newel at the flight start carries both rail ends: the landing rail comes in at its side,
    // and flight 2's rail leaves its top a riser higher, on the line the inner rail follows too.
    const flightStartY: number = flight2RailY(ctx, f2StartX);
    ctx.handrailPoints.push({ x: f2StartX, y: landingY + rc.height, z: f2Z });
    ctx.handrailPoints.push({ x: f2StartX, y: flightStartY, z: f2Z });
    ctx.posts.push({ position: { x: f2StartX, y: landingY, z: f2Z }, height: newelHeight(ctx, landingY, flightStartY), rotation: 0 });

    // Balusters along Z-edge (flight 1 side)
    const zEdgeLen: number = f2Z - turnStartZ;
    const zBalCount: number = balusterCount(zEdgeLen, rc);
    for (let i: number = 1; i < zBalCount; i++) {
        ctx.balusters.push({
            position: { x: f1X, y: landingY, z: turnStartZ + (zEdgeLen * i) / zBalCount },
            height: rc.height,
            rotation: 0,
        });
    }

    // Balusters along X-edge (flight 2 side)
    const xEdgeStart: number = Math.min(f1X, f2StartX);
    const xEdgeEnd: number = Math.max(f1X, f2StartX);
    const xEdgeLen: number = xEdgeEnd - xEdgeStart;
    const xBalCount: number = balusterCount(xEdgeLen, rc);
    for (let i: number = 1; i < xBalCount; i++) {
        ctx.balusters.push({
            position: { x: xEdgeStart + (xEdgeLen * i) / xBalCount, y: landingY, z: f2Z },
            height: rc.height,
            rotation: 0,
        });
    }
}

/**
 * Inner corner: redirect the handrail perpendicular to flight 2 and lift it to the flight-2 entry level.
 * Extracted from `generateLShapeRailings` to keep its complexity low.
 */
function pushInnerCorner(ctx: ILShapeRailingContext): void {
    const rc: IRailingConfig = ctx.rc;
    const { turnPostY, f2Z } = ctx;

    const innerCornerX: number = ctx.f1X;
    // The newel rises to flight 2's rail line, the same line the outer rail follows.
    const flightStartY: number = flight2RailY(ctx, innerCornerX);
    ctx.handrailPoints.push({ x: innerCornerX, y: turnPostY + rc.height, z: f2Z });
    ctx.posts.push({ position: { x: innerCornerX, y: turnPostY, z: f2Z }, height: newelHeight(ctx, turnPostY, flightStartY), rotation: 0 });
    ctx.handrailPoints.push({ x: innerCornerX, y: flightStartY, z: f2Z });
}

/**
 * Flight-2 balusters interpolated from the turn exit to the top post.
 * Extracted from `generateLShapeRailings` to keep its complexity low.
 */
function pushFlight2Balusters(ctx: ILShapeRailingContext, turnExitPt: IVec3, topPost: IBalusterGeometry, topPostTopY: number): void {
    if (ctx.flight2Steps.length === 0) {
        return;
    }
    const rc: IRailingConfig = ctx.rc;
    const f2hAx: number = turnExitPt.x;
    const f2hAy: number = turnExitPt.y;
    const f2hBx: number = topPost.position.x;
    const f2hBy: number = topPostTopY;
    const f2SpanX: number = f2hBx - f2hAx;
    const toward: number = Math.sign(f2SpanX);
    const flight: { stringerType: IStairConfig['stringerType']; axis: 'x' } = { stringerType: ctx.config.stringerType, axis: 'x' };
    // On the inner side a landing's support post stands at the turn exit; the row starts at its face.
    const startX: number = f2hAx + (ctx.config.turnType === 'landing' && !ctx.isOuter ? (toward * rc.postWidth) / 2 : 0);
    for (const bx of treadBalusterRuns(startX, f2hBx - (toward * rc.postWidth) / 2, ctx.flight2Steps, flight, rc)) {
        const frac: number = (bx - f2hAx) / f2SpanX;
        const si: number = Math.min(Math.floor(frac * ctx.flight2Steps.length), ctx.flight2Steps.length - 1);
        const baseY: number = ctx.flight2Steps[si].position.y;
        const hY: number = f2hAy + frac * (f2hBy - f2hAy);
        ctx.balusters.push({ position: { x: bx, y: baseY, z: ctx.f2Z }, height: Math.max(rc.height / 2, hY - baseY), rotation: 0 });
    }
}

/**
 * X of the flight-1 rail line. On the inner side of a landing the one newel stands clear of the
 * flight-2 nose that overhangs the landing edge.
 */
function flight1RailX(base: ILShapeRailingBase, side: 'left' | 'right', isOuter: boolean, halfPost: number): number {
    const innerShift: number = !isOuter && !base.isWinderTurn ? (base.flight2Steps.at(0)?.noseLength ?? 0) : 0;
    return side === 'left' ? halfPost + innerShift : base.sw - halfPost - innerShift;
}

/**
 * The top post of flight 2: whole on the last tread where the tread lies under its whole square,
 * else past the exit edge at the slab top — where settling would move it: the baluster row ends at
 * its face.
 */
function flight2TopPost(base: ILShapeRailingBase, f2Z: number, xDir: number): IBalusterGeometry {
    const rc: IRailingConfig = base.rc;
    const lastF2: IStepGeometry = base.flight2Steps[base.flight2Steps.length - 1];
    const topFloor: number = base.config.floorHeight + base.config.floorThickness;
    const edgeX: number = centerX(lastF2) + (xDir * lastF2.treadDepth) / 2;
    const onTreadX: number = edgeX - (xDir * rc.postWidth) / 2;
    const onTread: boolean = [-1, 1].every((dx: number) =>
        [-1, 1].every((dz: number) =>
            insideOutline(treadFootprint(lastF2, base.config.stringerType), {
                x: onTreadX + (dx * rc.postWidth) / 2,
                z: f2Z + (dz * rc.postWidth) / 2,
            })
        )
    );
    const topBase: number = onTread ? lastF2.position.y : topFloor;
    return {
        position: { x: onTread ? onTreadX : edgeX + (xDir * rc.postWidth) / 2, y: topBase, z: f2Z },
        height: topFloor + rc.height - topBase,
        rotation: 0,
    };
}

/**
 * Builds the full railing for one side (flight 1 → turn → flight 2).
 * Extracted from `generateLShapeRailings` to keep its complexity low.
 */
function buildSideRailing(base: ILShapeRailingBase, side: 'left' | 'right'): IRailingGeometry {
    const { rc, sw, isRight, turnStartZ, turnPostY, entryZ, firstStepY } = base;

    // Outer = geometric outer of turn (longer radius): LEFT for right turn, RIGHT for left turn
    // Inner = geometric inner (shorter radius, simple corner): RIGHT for right turn, LEFT for left turn
    const isOuter: boolean = isRight ? side === 'left' : side === 'right';
    // Distance of the rail line from the stair edge: the тетива axis, or half a post over a kosour.
    const halfPost: number = railLineOffset(base.config.stringerType, rc);
    const f1X: number = flight1RailX(base, side, isOuter, halfPost);
    const f2Z: number = isOuter ? turnStartZ + sw - halfPost : turnStartZ + halfPost;
    const f2StartX: number = isRight ? sw : 0;
    // Bottom post reaches the handrail level above the first stair tread so the
    // handrail starts coplanar with the first step, not one riser below.
    const bottomPostTopY: number = 0 + (firstStepY + rc.height);
    const xDir: number = isRight ? 1 : -1;

    // ── Top post: placed first, since flight 2's rail line ends on it ──
    const topPost: IBalusterGeometry = flight2TopPost(base, f2Z, xDir);

    const ctx: ILShapeRailingContext = {
        ...base,
        side,
        isOuter,
        halfPost,
        f1X,
        f2Z,
        f2StartX,
        f1hAz: entryZ,
        f1hAy: bottomPostTopY,
        f1hBz: turnStartZ,
        f1hBy: turnPostY + rc.height,
        xDir,
        f2Rail: lShapeFlight2Rail(base, xDir, topPost),
        balusters: [],
        posts: [],
        handrailPoints: [],
    };

    // ── Bottom post ──
    ctx.posts.push({ position: { x: ctx.f1X, y: 0, z: ctx.f1hAz }, height: ctx.f1hAy, rotation: 0 });

    // ── Flight 1: handrail from bottom post top to turn entry ──
    ctx.handrailPoints.push({ x: ctx.f1X, y: ctx.f1hAy, z: ctx.f1hAz });
    pushFlight1Balusters(ctx);

    if (ctx.isOuter && ctx.isWinderTurn) {
        pushWinderTurnRailing(ctx);
    } else {
        ctx.handrailPoints.push({ x: ctx.f1X, y: ctx.turnPostY + rc.height, z: ctx.turnStartZ });

        if (ctx.isOuter) {
            ctx.posts.push({ position: { x: ctx.f1X, y: ctx.turnPostY, z: ctx.turnStartZ }, height: rc.height, rotation: 0 });
            pushLandingEdges(ctx);
        } else {
            // The inner corner newel alone carries the turn: a second post half a post away
            // would stand in it.
            pushInnerCorner(ctx);
        }
    }

    ctx.posts.push(topPost);
    const topPostTopY: number = topPost.position.y + topPost.height;

    // ── Flight 2: handrail from turn exit to top post, balusters interpolated ──
    const turnExitPt: IVec3 = ctx.handrailPoints[ctx.handrailPoints.length - 1];
    ctx.handrailPoints.push({ x: topPost.position.x, y: topPostTopY, z: topPost.position.z });
    pushFlight2Balusters(ctx, turnExitPt, topPost, topPostTopY);

    return {
        side: ctx.side,
        balusters: ctx.balusters,
        posts: ctx.posts,
        handrail: { points: ctx.handrailPoints, width: rc.handrailWidth, height: rc.handrailHeight },
        balusterWidth: rc.balusterWidth,
        postWidth: rc.postWidth,
    };
}

export function generateLShapeRailings(
    flight1Steps: IStepGeometry[],
    turnSteps: IStepGeometry[],
    flight2Steps: IStepGeometry[],
    config: IStairConfig,
    turnStartZ: number
): IRailingGeometry[] {
    const railings: IRailingGeometry[] = [];
    const rc: IRailingConfig = config.railing;
    const sw: number = config.stairWidth;
    const isRight: boolean = config.turnDirection === 'right';
    if (flight2Steps.length === 0) {
        return railings;
    }

    // With turnAtStep === 0 there is no flight 1 — the user enters straight on
    // the winders/landing. Anchor the railing to the first available step so
    // the bottom post and the first handrail point sit at the same height
    // (otherwise the handrail drops by one riser between the post top and the
    // turn entry).
    const sides: ('left' | 'right')[] = rc.sides === 'both' ? ['left', 'right'] : [rc.sides];
    const stepAnchors: ILShapeStepAnchors = resolveStepAnchors(flight1Steps, turnSteps, flight2Steps);
    const turnLevels: ILShapeTurnLevels = resolveTurnLevels(flight1Steps, turnSteps, stepAnchors.firstStepY, config);

    const base: ILShapeRailingBase = {
        rc,
        config,
        sw,
        isRight,
        turnStartZ,
        flight1Steps,
        turnSteps,
        flight2Steps,
        ...stepAnchors,
        ...turnLevels,
    };

    for (const side of sides) {
        railings.push(buildSideRailing(base, side));
    }

    return railings;
}
